import {
  GetUserProgressResponse,
  IDDocumentType,
} from "@amzn/client-workbench-api-model";
import { ActivatedRegions } from "src/amplifyStageConfig";
import { FeatureFlagResponse } from "src/global";

import { VerificationStatus } from "@/apis/IdentityCheckHelper";
import {
  DAY1_ONBOARDING_STEPS,
  GLOBAL_DEFAULT_CHAT_SUPPORT_REF_PAGE,
  Step,
  STEP_ID,
  VideoRecordingSubmission,
} from "@/components/constants";
import { ChatSupportContextReason } from "@/components/GetSupport/ChatWithITSupport/constants";
import {
  RefLinkValue,
  RefPageValue,
} from "@/components/GetSupport/ChatWithITSupport/types";
import { getCurrentStepNumFromCache } from "@/helpers/CachedStepProgress";
import { isCredentialCachingAvailable } from "@/helpers/electron-helper";
import { getOS } from "@/helpers/os-helper";
import { isUsbDetectionAvailable } from "@/hooks/useUsbDevices";
import { LOCALES } from "@/i18n/locales";

/**
 * Returns a filtered list of the steps that are relevant to display for this platform and environment.
 *
 * @param steps A full step list.
 * @param featureFlags Optional feature flags response. When provided, steps with a
 *   `featureFlag` field are only included if that flag is enabled. When omitted,
 *   feature-flag-gated steps are excluded by default.
 */
export const getEnabledSteps = (
  steps: Step[],
  featureFlags?: FeatureFlagResponse
) => {
  const OS = getOS();
  return steps.filter((step) => {
    if (step.platforms && !step.platforms.includes(OS)) {
      return false;
    }

    if (
      step.id === STEP_ID.INSERT_AND_TEST_SECURITY_KEY &&
      !isUsbDetectionAvailable()
    ) {
      return false;
    }

    // The Amazon Account Caching step relies on a native Electron hook to cache
    // domain credentials. Only show it when that capability is available (i.e.
    // running in an Electron app version that exposes it). Combined with the
    // Windows platform gate above, this keeps the step hidden in the browser
    // and on older Electron builds.
    if (
      step.id === STEP_ID.AMAZON_ACCOUNT_CACHING &&
      !isCredentialCachingAvailable()
    ) {
      return false;
    }

    // Generic feature flag gate: if a step declares a featureFlag, it is only
    // included when that flag is explicitly enabled in the API response.
    if (step.featureFlag) {
      const enabled = featureFlags?.[step.featureFlag]?.enabled ?? false;
      if (!enabled) return false;
    }

    return true;
  });
};

export type WizardPathType = "IVV" | "ManualRIV" | undefined;

/**
 * Alias of the optional onboarding progress object included in
 * GetUserProgress responses.
 */
export type OnboardingProgress = GetUserProgressResponse["onboardingProgress"];

/**
 * The app state interface
 */
export interface AppState {
  /**
   * The current locale for the page dictated by language selected by NH in the UI.
   */
  selectedLanguage: string;
  /**
   * The current step number that NH is working on. 1 based index.
   */
  currentStepNum: number;

  /**
   * The current step that NH is working on.
   */
  currentStep: Step;

  /**
   * All on-boarding steps that NHs will need to complete.
   */
  steps: Step[];

  /**
   * The network connection status in between NH's laptop and the internet.
   */
  isNetworkConnected: boolean;

  /**
   * The Amazon username that is assigned to the NH.
   */
  amazonUsername?: string;

  /**
   * The additional details about the user's onboarding progress
   * including which step they're on, if they've registered their security
   * key, etc.
   */
  onboardingProgress?: OnboardingProgress;

  /**
   * Number of the required documents needed to verify NH's ID.
   */
  requiredDocsWithGesturesCount: number;

  /**
   * The number of IDs that is legally required for verification
   */
  requiredDocsForHiringCountry: number;

  /**
   * All documents that NHs have submitted so far. Only video recordings are required from NHs for the Day1 wizard right
   * now.
   */
  documents: VideoRecordingSubmission[];

  /**
   * The verification status of the documents that NHs are required to submit.
   */
  docsVerificationStatus: VerificationStatus;

  /**
   * The selected primary Id issuing country and type selection for the Identity verification step
   */
  primaryIdSelection?: {
    /**
     * The selected primary Id issuing country for the Identity verification step
     */
    issuingCountry?: string;

    /**
     * The selected primary Id type for the Identity verification step
     */
    documentType?: string;
  };

  /**
   * Whether or not a secondary ID was provided if required
   */
  secondaryIdProvided?: string;

  /**
   * Whether or not a secondary ID is government-issued or not if required
   */
  secondaryIdGovIssued?: boolean;

  /**
   * The hiring country of the new hire for the Identity verification step
   */
  hiringCountry?: string;

  /**
   * Whether the record that carries {@link hiringCountry} has come back, either with a country or
   * without one. True does not mean a country is known -- it means waiting for one is over, so the
   * region an AI session records to can be decided and pinned.
   */
  isHiringRegionSettled: boolean;

  /**
   * If Amplify is configured yet.
   */
  amplifyConfigured: boolean;

  /**
   *  The chat support context to use.
   */
  nonDirectedChatSupportContext?: ChatSupportContextReason;

  /**
   * The active region that controls which regional endpoint to use
   * for API calls and which Cognito auth config to use
   */
  activeRegion?: ActivatedRegions;

  wizardPath: {
    /**
     * The previous wizard path type that users are routed to.
     */
    previous: WizardPathType;

    /**
     * The wizard path type that users are currently on.
     */
    current: WizardPathType;
  };

  /**
   * If the chat support flyout is visible
   */
  chatSupportFlyoutVisible: boolean;

  /**
   * If the chatbot flyout is visible (AI onboarding assistant)
   */
  chatbotFlyoutVisible: boolean;

  /**
   * If the get support flyout is visible
   */
  getSupportFlyoutVisible: boolean;

  /**
   * If the get support with IDV flyout is visible
   */
  getIdvSupportFlyoutVisible: boolean;

  /**
   * The chat support ref page. Provides context to IT support about where the user
   * is in their journey. Always has a value following hierarchical defaults:
   * Global Default ("ezo-login") -> Step Default -> Component Specific -> Error State
   */
  chatSupportRefPage: RefPageValue;

  /**
   * The chat support ref link value
   */
  chatSupportRefLink?: RefLinkValue;

  /**
   * Whether the chat support session has been initialized (chat widget loaded).
   * Once true, the flyout shows the ChatWidget instead of FAQ or Start Chat button.
   */
  chatInitialized: boolean;
}

/**
 * The Action interface for updating the app state in the AppProvider.
 */
export interface Action {
  /**
   * The type of the action
   */
  type: ActionType;

  /**
   * The step ID of the step which sends the action
   */
  stepId?: string;

  /**
   * The value of the locale associated with a language selected by user in UI
   */
  selectedLanguage?: string;
  /**
   * The value of the network connection status that will be used to update the isNetworkConnected value in the app
   * state
   */
  isNetworkConnected?: boolean;

  /**
   * Should the next button be enabled.
   */
  enableNext?: boolean;

  /**
   * The username that is assigned to the NH.
   */
  amazonUsername?: string;

  /**
   * The document submitted by NHs. We only require video recordings from NHs for the Day1 on-boarding wizard.
   */
  document?: VideoRecordingSubmission;

  /**
   * NHE-5636: Document types to remove from the documents list (for retry flow).
   */
  removeDocumentTypes?: string[];

  /**
   * The recording type whose ID still was uploaded and should be cleared.
   */
  clearIdStillDocumentType?: IDDocumentType;

  /**
   * The required documents number needed to be submitted by NHs.
   */
  requiredDocsWithGesturesCount?: number;

  /**
   * The legally required number of documents for ID verification in the NH's hiring country
   */
  requiredDocsForHiringCountry?: number;

  /**
   * Should the next button be hidden.
   */
  hideNext?: boolean;

  /**
   * The verification status of the submitted documents in the Identity Check step.
   */
  docsVerificationStatus?: VerificationStatus;

  /**
   * The selected primary Id issuing country and type selection for the Identity verification step
   */
  primaryIdSelection?: {
    /**
     * The selected primary Id issuing country for the Identity verification step
     */
    issuingCountry?: string;

    /**
     * The selected primary Id type for the Identity verification step
     */
    documentType?: string;
  };

  /**
   * Whether or not a secondary ID was provided if required
   */
  secondaryIdProvided?: string;

  /**
   * Whether or not a secondary ID is government-issued or not if required
   */
  secondaryIdGovIssued?: boolean;

  /**
   * The hiring country of the new hire for verification.
   */
  hiringCountry?: string;

  /**
   * If amplify is configured yet
   */
  amplifyConfigured?: boolean;

  /**
   * The i18n id that will be used to map into the tooltip text shown on a disabled button.
   */
  disabledBtnToolTipI18nId?: string;

  /**
   * Sets the non directed chat support context. Used by the header chat support to set click metrics based on this property.
   */
  nonDirectedChatSupportContext?: ChatSupportContextReason;

  /**
   * The active region that controls which regional endpoint to use
   * for API calls and which Cognito auth config to use
   */
  activeRegion?: ActivatedRegions;

  wizardPath?: WizardPathType;

  /**
   * If the chat support flyout is visible
   */
  chatSupportFlyoutVisible?: boolean;

  /**
   * If the chatbot flyout is visible
   */
  chatbotFlyoutVisible?: boolean;

  /**
   * If the get support flyout is visible
   */
  getSupportFlyoutVisible?: boolean;

  /**
   * If the get support with IDV flyout is visible
   */
  getIdvSupportFlyoutVisible?: boolean;

  /**
   * The ref page to set. Always a string value (never undefined).
   * Use step defaults or "ezo-login" for generic context.
   */
  chatSupportRefPage?: RefPageValue;

  /**
   * The chat support link context.
   */
  chatSupportRefLink?: RefLinkValue;

  /**
   * The onboarding progress object from a GetUserProgress response
   */
  onboardingProgress?: OnboardingProgress;

  /**
   * Feature flags response, used by REBUILD_STEPS to re-evaluate
   * which steps should be visible.
   */
  featureFlags?: FeatureFlagResponse;
}

/**
 * Action enums that will be used to update the app state in the AppProvider
 */
export enum ActionType {
  /**
   * The action to reset to the initial state
   */
  RESET = "reset",

  /**
   * The action to change the language rendered on the UI based on user selection
   */
  SET_SELECTED_LANGUAGE = "setSelectedLanguage",
  /**
   * The action to enable/disable the next step of the target step
   */
  SET_NEXT_BTN_STATUS = "setNextBtnStatus",

  /**
   * The action to mark the target step as completed and increment the current step number by 1
   */
  NEXT_STEP = "nextStep",

  /**
   * The action to update the status of network connection of the NHs laptops
   */
  UPDATE_NETWORK = "updateNetwork",

  /**
   * The action to set the Amazon username assigned to the NH
   */
  SET_USERNAME = "setAmazonUsername",

  /**
   * The action to append the document to the existing documents list. NHs are required to upload video recordings only
   * for day1 on-boarding.
   */
  ADD_DOCUMENT = "addDocument",

  /**
   * The action to remove the last document to the existing documents list. NHs are required to upload video recordings
   * only for day1 on-boarding.
   */
  POP_DOCUMENT = "popDocument",

  /**
   * NHE-5636: Remove documents matching specific document types from the list.
   */
  REMOVE_DOCUMENTS_BY_TYPES = "removeDocumentsByTypes",

  /**
   * Remove an uploaded ID still from a recording in the app state, once the
   * whole submission has succeeded.
   */
  CLEAR_ID_STILL = "clearIdStill",

  /**
   * The action to show/hide the next button on each on-boarding step.
   */
  SET_HIDE_NEXT = "setHideNext",

  /**
   * The action to set the number of required documents that NHs need to submit to verify their IDs.
   */
  SET_REQUIRED_DOCS_NUM = "setRequiredDocsNum",

  /**
   * The action to set the number of documents legally required by the NH's hiring country for ID verification
   */
  SET_LEGALLY_REQUIRED_DOCS_NUM = "setLegallyRequiredDocsNum",

  /**
   * The action to set the verification status of the NH's submitted documents.
   */
  SET_DOCS_VERIFICATION_STATUS = "setDocsVerificationStatus",

  /**
   * Mark the last recorded document as submitted. It is used for indicating that NHs have clicked the submit button
   * but not the actual upload since we don't do the actual upload until we have collected all required documents.
   */
  SET_LAST_DOC_SUBMIT_CLICKED = "setLastDocSubmitClicked",

  /**
   * The action to set the primary ID issuing country and type selected for ID verification.
   */
  SET_PRIMARY_ID_SELECTION = "setPrimaryIdSelection",

  /**
   * The action to set whether or not the secondary ID was provided if required.
   */
  SET_SECONDARY_ID_PROVIDED = "setSecondaryIdProvided",

  /**
   * The action to set whether the secondary ID is government-issued or not.
   */
  SET_SECONDARY_ID_GOV_ISSUED = "setSecondaryIdSelection",

  /**
   * The action to set the hiring country of the new hire for ID verification.
   */
  SET_HIRING_COUNTRY = "setHiringCountry",

  /**
   * If Amplify is configured yet. It should be configured on start however if components are loaded which immediately try to perform API calls, it will fail.
   */
  SET_AMPLIFY_CONFIGURED = "setAmplifyConfigured",

  /**
   * Set non directed chat support context.
   */
  SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT = "setNonDirectedChatSupportContext",

  /**
   * Set the API active region.
   */
  SET_API_ACTIVE_REGION = "setApiActiveRegion",

  SET_WIZARD_PATH = "setWizardPath",

  /**
   * Sets the chat support flyout to visible/open.
   */
  SET_CHAT_SUPPORT_FLYOUT_VISIBLE = "setChatSupportFlyoutVisible",

  /**
   * Sets the chatbot flyout to visible/open.
   */
  SET_CHATBOT_FLYOUT_VISIBLE = "setChatbotFlyoutVisible",

  /**
   * Sets the get support flyout to visible.
   */
  SET_GET_SUPPORT_FLYOUT_VISIBLE = "setGetSupportFlyoutVisible",

  /**
   * Sets the get support with IDV flyout to visible.
   */
  SET_GET_IDV_SUPPORT_FLYOUT_VISIBLE = "setGetIdvSupportFlyoutVisible",

  /**
   * Sets the chat support ref page
   */
  SET_CHAT_SUPPORT_REF_PAGE = "setChatSupportRefPage",

  /**
   * Sets the chat support link string. This indicates where the origin of the request was, inline IT support help or clicking Get Support
   */
  SET_CHAT_SUPPORT_REF_LINK = "setChatSupportRefLink",

  /**
   * Sets the chat initialized flag when the chat widget is loaded for the first time.
   */
  SET_CHAT_INITIALIZED = "setChatInitialized",

  /*
   * Sets the onboarding progress after getting it from a GetUserProgress response
   */
  SET_ONBOARDING_PROGRESS = "SetOnboardingProgress",

  /**
   * Rebuild the step list when feature flags become available, adding or
   * removing steps that are gated behind a feature flag.
   */
  REBUILD_STEPS = "rebuildSteps",
}

/**
 * Get the initial steps with cached state applied (filtered for current environment)
 */
const getInitialSteps = () => {
  const cachedStepIndex = getCurrentStepNumFromCache();
  const enabledSteps = getEnabledSteps(DAY1_ONBOARDING_STEPS);

  return enabledSteps.map((step, index) => {
    // In the case where the wizard resumes from a cached state, the action that advances through the PREVIOUS steps
    // never triggers, meaning the `completed` property on each step is not marked as such, so the wizard does not display the checkmark
    // for the previous steps, among other logic being broken. Instead, mark all previous steps as completed.
    if (index < cachedStepIndex) {
      return {
        ...step,
        completed: true,
        hideNext: false,
        nextDisabled: false,
      };
    }
    return step;
  });
};

/**
 * The initial App state that will be loaded into the AppProvider context.
 */
export const INITIAL_STATE: AppState = {
  /**
   * The initial language selected by NH, default to English
   */
  selectedLanguage: localStorage.getItem("locale") ?? LOCALES.ENGLISH,
  /**
   * The initial step number (one-based, based on enabled steps)
   */
  currentStepNum: getCurrentStepNumFromCache() + 1,

  /**
   * The initial step
   */
  currentStep: getEnabledSteps(DAY1_ONBOARDING_STEPS)[
    getCurrentStepNumFromCache()
  ],

  /**
   * All steps that we will be showing on the Day1 on-boarding wizard (filtered for current environment)
   */
  steps: getInitialSteps(),

  /**
   * The network connection status in between the NH's laptop and the internet
   */
  isNetworkConnected: false,

  /**
   * Number of the required documents that NHs need to submit. Default to 0.
   */
  requiredDocsWithGesturesCount: 0,

  /**
   * Number of documents legally required by the NH's hiring country, Default to 2 (requirement for U.S.)
   */
  requiredDocsForHiringCountry: 2,

  /**
   * Documents that NHs have submitted so far. It only consists of video recordings for the Day1 wizard.
   */
  documents: [],

  /**
   * The verification status of the documents that NHs are required to submit. Default to NOT_STARTED.
   */
  docsVerificationStatus: VerificationStatus.NOT_STARTED,

  /**
   * If Amplify is configured yet.
   */
  amplifyConfigured: false,

  /* Nothing is known about the hiring region until the progress record settles. */
  isHiringRegionSettled: false,

  /**
   * The active region that controls which regional endpoint to use
   * for API calls and which Cognito auth config to use
   */
  activeRegion: undefined,

  /**
   * If the chat support flyout is visible
   */
  chatSupportFlyoutVisible: false,

  /**
   * If the chatbot flyout is visible
   */
  chatbotFlyoutVisible: false,

  /**
   * If the Get Support flyout is open
   */
  getSupportFlyoutVisible: false,

  /**
   * If the Get Support with IDV flyout is open
   */
  getIdvSupportFlyoutVisible: false,

  /**
   * The chat support ref page. Initialized to the current step's default or global default.
   * This follows a hierarchical fallback pattern:
   * Global Default ("PageLevel") -> Step Default -> Component Specific -> Error State
   */
  chatSupportRefPage:
    getEnabledSteps(DAY1_ONBOARDING_STEPS)[getCurrentStepNumFromCache()]
      ?.defaultChatSupportRefPage ?? GLOBAL_DEFAULT_CHAT_SUPPORT_REF_PAGE,

  wizardPath: {
    /**
     * The previous wizard path type that users are routed to.
     */
    previous: undefined,

    /**
     * The wizard path type that users are currently on.
     */
    current: undefined,
  },

  /**
   * Whether the chat support session has been initialized.
   */
  chatInitialized: false,
};
